import { formatSignalLabelFromKey, getSignalVariant } from './signalUtils';
import { getSignalColorFromDefinition } from './signalColorUtils';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toCount = (value) => {
  if (typeof value === 'string') {
    return toNumber(value.replace(/,/g, '').trim());
  }
  return toNumber(value);
};

const toIso = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
};

const inferPolarity = (trend) => {
  const explicit = typeof trend?.polarity === 'string' ? trend.polarity.toLowerCase() : null;
  if (explicit === 'positive' || explicit === 'negative') return explicit;

  const variant = getSignalVariant(null, trend?.metric_name);
  if (variant === 'info') return 'positive';
  if (variant === 'danger') return 'negative';
  return 'negative';
};

const getSignalLabel = (trend) => (
  trend?.display_name
  || formatSignalLabelFromKey(trend?.metric_name)
  || trend?.metric_name
  || 'Unknown Signal'
);

const normalizeSeries = (series) => (
  (Array.isArray(series) ? series : [])
    .map((point) => ({
      bucket: toIso(point?.bucket),
      detected: toNumber(point?.detected),
      total: toNumber(point?.total),
    }))
    .filter(point => point.bucket)
    .sort((a, b) => new Date(a.bucket).getTime() - new Date(b.bucket).getTime())
);

const splitWindowDelta = (series) => {
  if (!series.length) {
    return { recentCount: 0, previousCount: 0, delta: 0 };
  }

  const midpoint = Math.max(1, Math.floor(series.length / 2));
  const previous = series.slice(0, midpoint);
  const recent = series.slice(midpoint);

  const previousCount = previous.reduce((sum, point) => sum + point.detected, 0);
  const recentCount = recent.reduce((sum, point) => sum + point.detected, 0);

  return {
    recentCount,
    previousCount,
    delta: recentCount - previousCount,
  };
};

const getLastSeen = (series) => {
  for (let idx = series.length - 1; idx >= 0; idx -= 1) {
    if (series[idx].detected > 0) return series[idx].bucket;
  }
  return null;
};

export const buildSignalScorecard = (classifierTrends, workflowDistribution = [], bucketSize = null) => {
  const normalizedBucketSize = typeof bucketSize === 'string'
    ? bucketSize.trim().toLowerCase()
    : null;
  const workflowTopBySignal = new Map();
  workflowDistribution.forEach((workflow) => {
    (workflow?.topSignals || []).forEach((signal) => {
      const signalCount = toNumber(signal?.count);
      const signalKey = `${signal?.key || ''}`.toLowerCase();
      const signalLabelKey = `${signal?.label || ''}`.toLowerCase();
      if (signalKey) {
        const current = workflowTopBySignal.get(signalKey);
        if (!current || signalCount > current.count) {
          workflowTopBySignal.set(signalKey, {
            workflowName: workflow?.workflowName || 'Unknown Business Task',
            count: signalCount,
          });
        }
      }
      if (signalLabelKey) {
        const currentByLabel = workflowTopBySignal.get(signalLabelKey);
        if (!currentByLabel || signalCount > currentByLabel.count) {
          workflowTopBySignal.set(signalLabelKey, {
            workflowName: workflow?.workflowName || 'Unknown Business Task',
            count: signalCount,
          });
        }
      }
    });
  });

  return (Array.isArray(classifierTrends) ? classifierTrends : []).map((trend) => {
    const series = normalizeSeries(trend?.data);
    const totals = series.reduce((acc, point) => ({
      detected: acc.detected + point.detected,
      total: acc.total + point.total,
    }), { detected: 0, total: 0 });
    const fallbackDelta = splitWindowDelta(series).delta;
    const delta = trend?.comparison_available === false
      ? null
      : trend?.delta_detected_count != null
        ? toNumber(trend.delta_detected_count)
        : fallbackDelta;
    const polarity = inferPolarity(trend);
    const signalKey = `${trend?.metric_name || ''}`.toLowerCase();
    const label = getSignalLabel(trend);
    const workflowInfo = workflowTopBySignal.get(signalKey) || workflowTopBySignal.get(label.toLowerCase());

    return {
      key: trend?.metric_name || signalKey,
      label,
      description: trend?.description || '',
      classifierVersion: trend?.classifier_version || null,
      eligibleCount: toCount(trend?.eligible_count),
      detectedCount: toCount(trend?.detected_count),
      polarity,
      signalColor: getSignalColorFromDefinition({
        metric_name: trend?.metric_name,
        display_name: trend?.display_name,
        name: label,
        polarity,
      }),
      tags: Array.isArray(trend?.tags) ? trend.tags : [],
      totalDetected: totals.detected,
      totalEvaluated: totals.total,
      detectionRate: totals.total > 0 ? totals.detected / totals.total : null,
      delta,
      absDelta: delta == null ? 0 : Math.abs(delta),
      lastSeen: getLastSeen(series),
      trendPoints: series.map((point) => ({ bucket: point.bucket, value: point.detected })),
      topWorkflow: workflowInfo?.workflowName || null,
      topWorkflowCount: workflowInfo?.count || 0,
      classifierType: trend?.metric_name || signalKey,
      bucketSize: normalizedBucketSize,
    };
  });
};

export const buildWorkflowDistribution = (issues = [], positives = []) => {
  const workflows = new Map();

  const canonicalSignalKey = (value) => `${value || ''}`.trim().toLowerCase();
  const canonicalSignalLabel = (key, label) => {
    const normalizedLabel = formatSignalLabelFromKey(key) || label || key || '';
    return canonicalSignalKey(normalizedLabel);
  };
  const inferDefaultPolarity = (signalKey, fallbackPolarity) => {
    if (fallbackPolarity === 'positive' || fallbackPolarity === 'negative') return fallbackPolarity;
    const variant = getSignalVariant(null, signalKey);
    if (variant === 'info') return 'positive';
    if (variant === 'danger') return 'negative';
    return 'negative';
  };

  const normalizeSignalsFromTrace = (trace, defaultPolarity) => {
    const byKey = new Map();

    const upsert = ({ key, label, polarity }) => {
      const resolvedLabel = (label || formatSignalLabelFromKey(key) || key || '').trim();
      const canonical = canonicalSignalLabel(key, resolvedLabel);
      if (!canonical) return;
      const normalizedPolarity = inferDefaultPolarity(canonical, polarity || defaultPolarity);

      const existing = byKey.get(canonical);
      if (!existing) {
        byKey.set(canonical, {
          key: canonical,
          label: resolvedLabel || formatSignalLabelFromKey(canonical) || canonical,
          polarity: normalizedPolarity,
        });
        return;
      }

      // Prefer more descriptive labels when available.
      if ((!existing.label || existing.label === canonical) && resolvedLabel) {
        existing.label = resolvedLabel;
      }
      if (!existing.polarity && normalizedPolarity) {
        existing.polarity = normalizedPolarity;
      }
    };

    const detectedClassifiers = Array.isArray(trace?.detected_classifiers) ? trace.detected_classifiers : [];
    detectedClassifiers.forEach((classifier) => {
      if (typeof classifier === 'string') {
        const key = classifier.trim();
        if (!key) return;
        upsert({
          key,
          label: formatSignalLabelFromKey(key) || key,
          polarity: defaultPolarity,
        });
        return;
      }

      const key = `${classifier?.classifier_key || classifier?.key || classifier?.name || ''}`.trim();
      if (!key) return;
      upsert({
        key,
        label: classifier?.name || formatSignalLabelFromKey(classifier?.classifier_key || classifier?.key) || key,
        polarity: classifier?.polarity || defaultPolarity,
      });
    });

    const detections = Array.isArray(trace?.detections) ? trace.detections : [];
    detections.forEach((detection) => {
      const key = `${detection?.classifier_key || detection?.name || ''}`.trim();
      if (!key) return;
      upsert({
        key,
        label: detection?.name || formatSignalLabelFromKey(detection?.classifier_key) || key,
        polarity: detection?.polarity || defaultPolarity,
      });
    });

    const legacyKeys = [
      ...(Array.isArray(trace?.signals) ? trace.signals : []),
      ...(Array.isArray(trace?.signal) ? trace.signal : []),
      ...(Array.isArray(trace?.problem_types) ? trace.problem_types : []),
    ];
    legacyKeys.forEach((signalKey) => {
      const key = `${signalKey || ''}`.trim();
      if (!key) return;
      upsert({
        key,
        label: formatSignalLabelFromKey(key) || key,
        polarity: defaultPolarity,
      });
    });

    return [...byKey.values()];
  };

  const addTrace = (trace, polarity) => {
    const workflowName = trace?.workflow_name || 'Unknown Business Task';
    const key = workflowName.trim() || 'Unknown Business Task';

    if (!workflows.has(key)) {
      workflows.set(key, {
        workflowName: key,
        negativeCount: 0,
        positiveCount: 0,
        signalCounts: new Map(),
        uniqueTraceIds: new Set(),
        positiveTraceIds: new Set(),
        negativeTraceIds: new Set(),
        seenSignalsByTrace: new Map(),
      });
    }

    const entry = workflows.get(key);
    const traceId = `${trace?.trace_id || trace?.id || ''}`.trim();
    if (traceId) {
      entry.uniqueTraceIds.add(traceId);
      if (polarity === 'positive') {
        if (!entry.positiveTraceIds.has(traceId)) {
          entry.positiveTraceIds.add(traceId);
          entry.positiveCount += 1;
        }
      } else if (!entry.negativeTraceIds.has(traceId)) {
        entry.negativeTraceIds.add(traceId);
        entry.negativeCount += 1;
      }
    } else if (polarity === 'positive') {
      entry.positiveCount += 1;
    } else {
      entry.negativeCount += 1;
    }

    const signals = normalizeSignalsFromTrace(trace, polarity);
    const seenSignalsForTrace = traceId
      ? (entry.seenSignalsByTrace.get(traceId) || new Set())
      : null;

    signals.forEach((signal) => {
      const keyName = canonicalSignalLabel(signal?.key, signal?.label);
      if (!keyName) return;
      if (seenSignalsForTrace && seenSignalsForTrace.has(keyName)) return;

      const existing = entry.signalCounts.get(keyName) || {
        key: keyName,
        label: signal?.label || formatSignalLabelFromKey(keyName) || keyName,
        count: 0,
        polarity: inferDefaultPolarity(keyName, signal?.polarity || polarity),
      };
      existing.count += 1;
      if (!existing.polarity) {
        existing.polarity = inferDefaultPolarity(keyName, signal?.polarity || polarity);
      }
      entry.signalCounts.set(keyName, existing);
      if (seenSignalsForTrace) seenSignalsForTrace.add(keyName);
    });

    if (traceId) {
      entry.seenSignalsByTrace.set(traceId, seenSignalsForTrace);
    }
  };

  (Array.isArray(issues) ? issues : []).forEach(trace => addTrace(trace, 'negative'));
  (Array.isArray(positives) ? positives : []).forEach(trace => addTrace(trace, 'positive'));

  return [...workflows.values()]
    .map((entry) => {
      const total = entry.uniqueTraceIds.size > 0
        ? entry.uniqueTraceIds.size
        : entry.negativeCount + entry.positiveCount;
      const topSignals = [...entry.signalCounts.values()]
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return `${a.label || ''}`.localeCompare(`${b.label || ''}`);
        })
        .slice(0, 4);
      const signalCount = [...entry.signalCounts.values()].reduce((sum, signal) => sum + toCount(signal?.count), 0);
      const negativeSignalCount = [...entry.signalCounts.values()]
        .reduce((sum, signal) => (signal?.polarity === 'positive' ? sum : sum + toCount(signal?.count)), 0);

      return {
        workflowName: entry.workflowName,
        total,
        negativeCount: entry.negativeCount,
        positiveCount: entry.positiveCount,
        signalCount,
        negativeSignalCount,
        topSignals,
      };
    })
    .sort((a, b) => {
      const bNegativeSignals = toCount(b?.negativeSignalCount ?? b?.negative_signal_count);
      const aNegativeSignals = toCount(a?.negativeSignalCount ?? a?.negative_signal_count);
      if (bNegativeSignals !== aNegativeSignals) return bNegativeSignals - aNegativeSignals;

      const bNegative = toCount(b?.negativeCount ?? b?.negative_count);
      const aNegative = toCount(a?.negativeCount ?? a?.negative_count);
      if (bNegative !== aNegative) return bNegative - aNegative;

      const bTotal = toCount(b?.total ?? b?.total_traces ?? b?.totalTraces);
      const aTotal = toCount(a?.total ?? a?.total_traces ?? a?.totalTraces);
      if (bTotal !== aTotal) return bTotal - aTotal;

      return a.workflowName.localeCompare(b.workflowName);
    });
};
